
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

import moment from "moment"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { Case, Field } from "@/types"
import { Insight, InsightCalculation, InsightDetails, InsightSummary, LabelFormat } from "@/types/insights"
import { caseColumns, CaseColumn } from "@/types/caseColumns"
import SkeletonLoader from "../SkeletonLoader.vue"
import vSelect from "vue-select"

import api from "@/services/api"

import TableResults from "@/components/Insights/TableResults.vue"
import CaseTable from "@/components/CaseList/CaseTable.vue"

const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"skeleton": SkeletonLoader,
		"results-table": TableResults,
		"case-table": CaseTable,
		"v-select": vSelect
	}
})
export default class InsightTopResult extends Vue {
	@Prop()
	value: any;

	@Prop()
	edit!: boolean

	@setup.Action loadFieldsIfUnloaded!: () => Promise<Field[]>
	@setup.State fields!: Field[];

	loading: boolean = true;

	insights: InsightSummary[] = [];
	tableColumns: any[] = [];

	selectedInsight: InsightDetails | null = null;

	selectedInsightId: string | null = null;
	orderBy: string = "";
	selectedColumns: string[] = [];

	topValueResult: any = null;

	labelFormat: LabelFormat = LabelFormat.Text;

	groupFields = [
		{
			key: "label"
		},
		{
			key: "value"
		}
	]

	public get columnOptions () {
		if (this.selectedInsight) {
			var options: any[] = []
			if (this.selectedInsight.calculations) {
				this.selectedInsight.calculations.forEach((c:any, index: number) => {
					options.push({
						key: "calculation" + c.groupBy,
						label: c.label
					})
				})
			}

			return this.tableColumns.concat(options)
		}
		return []
	}

	public get calculationOptions () {
		return this.selectedInsight
			? this.selectedInsight.calculations.filter(c => !c.groupBy)
			: []
	}

	public get groupedResults () {
		return this.topValueResult
			? this.selectedColumns.map(column => {
				var calculation = this.selectedInsight!.calculations.find(c => c.key === column)
				if (!calculation) {
					return []
				}

				if (!calculation.groupBy) {
					return [{
						label: calculation.label,
						value: this.topValueResult.calculations[column]
					}]
				}

				return calculation.groupByKeyLabels
					? Object.keys(calculation.groupByKeyLabels as object).map(key => {
						return {
							label: calculation!.label + " - " + calculation!.groupByKeyLabels![key],
							value: this.topValueResult.calculations[column]
								? this.topValueResult.calculations[column][key]
								: 0
						}
					})
					: []
			}).flat()
			: []
	}

	public get valueLabel () {
		if (!this.topValueResult) return ""

		const label = this.topValueResult.label
		switch (this.labelFormat) {
			case LabelFormat.Date: {
				if (!label) return "Not set"
				const momentDate = moment(label)
				return momentDate.isValid() ? momentDate.format("L") : label
			}
			default:
				return label === null || label === "" ? "None" : label.toString()
		}
	}

	public async mounted () {
		if (this.edit) {
			this.insights = await api.insightsGet()

			if (!this.value || this.value.insightId === undefined) {
				this.$emit("input", {
					insightId: null,
					orderBy: null,
					columns: null
				})
			}
		}

		if (this.value && this.value.insightId && this.value.orderBy && this.value.columns) {
			this.selectedInsightId = this.value.insightId
			this.orderBy = this.value.orderBy
			this.selectedColumns = this.value.columns

			this.selectedInsight = await api.insightGet(this.value.insightId, true)
		}
		this.onEditChanged()
	}

	@Watch("edit")
	async onEditChanged () {
		this.loading = true
		if (this.edit) {
			this.insights = await api.insightsGet()

			if (!this.value || this.value.insightId === undefined) {
				this.$emit("input", {
					insightId: null,
					orderBy: null,
					columns: null
				})
			}

			// this.loadColumns()
		} else if (!this.edit && this.selectedInsight) {
			const insightData = await api.insightTopValue(this.selectedInsight!.id!, this.orderBy)

			if (!insightData || !this.selectedInsight.calculations) {
				return
			}

			this.labelFormat = insightData.labelFormat

			if (this.selectedInsight.calculations) {
				this.selectedInsight.calculations.forEach((calculation: InsightCalculation) => {
					calculation.groupByKeyLabels = insightData.calculationGroupByLabels[calculation.key]
				})
			}

			if (insightData.results && insightData.results.length > 0) {
				this.topValueResult = insightData.results[0]

				// Adds any calculation results to the top Value
				for (const calculationType in this.topValueResult.calculations) {
					// For each calculation results
					for (const fieldId in this.topValueResult.calculations[calculationType]) {
						// Loop through all properties in the top value results
						for (const prop in this.topValueResult.results[0]) {
							// If the current property is the calculation
							if (this.topValueResult.results[0][prop] === fieldId) {
								// Add a property to to the top Value for the calculation value
								this.topValueResult.results[0]["calculation" + prop] = this.topValueResult.calculations[calculationType][fieldId]
							}
						}
					}
				}
			} else {
				this.topValueResult = null
			}

			this.loadColumns()

			if (this.selectedInsight.calculations) {
				// Add columns to the table for each calculation
				this.selectedInsight.calculations.forEach((calculation: any, index: number) => {
					this.tableColumns.push({
						key: "calculation" + calculation.groupBy,
						label: calculation.label,
						fieldDataType: 0,
						dataType: "text"
					})
				})
			}
		}

		this.loading = false
	}

	public async loadColumns () {
		const fields = (await this.loadFieldsIfUnloaded()) || []
		const allColumns = [
			...caseColumns,
			...fields.map(f => {
				return {
					label: f.title,
					key: "fields." + f.id,
					fieldId: f.id,
					dataType: f.dataType,
					dataSourceId: f.dataSourceId,
					dynamicField: true,
					fieldExtensions: f.extensions
				}
			})
		]
		this.tableColumns = this.selectedInsight && this.selectedInsight!.columns
			? allColumns.filter(c => this.selectedInsight!.columns.indexOf(c.key) > -1)
			: []
	}

	public async selectInsight (insightId: string) {
		this.selectedInsight = await api.insightGet(insightId, true)

		if (this.value) {
			Vue.set(this.value, "insightId", insightId)
			this.$emit("input", this.value)
		} else {
			this.$emit("input", {
				insightId
			})
		}
		this.orderBy = ""
		this.selectedColumns = []

		this.loadColumns()
	}

	public async selectOrderBy (orderBy: any) {
		Vue.set(this.value, "orderBy", orderBy)
		this.$emit("input", this.value)
	}

	public async selectColumns (columns: any) {
		Vue.set(this.value, "columns", columns)
		this.$emit("input", this.value)
	}

	public get filterColumns () {
		return this.selectedInsight!.groupBy
			? this.selectedInsight!.calculations.filter(c => this.selectedColumns.includes(c.key)).map(c => {
				return [{
					label: c.label,
					key: c.key
				} as CaseColumn]
			}).flat()
			: this.tableColumns.filter(c => this.selectedColumns.includes(c.key))
	}
}
